import * as yup from 'yup'
import dataParsing from 'dataParsing/fr'
import {
  isLevel1Category,
  raceCategoriesLevel1
} from 'helpers/raceCategoryLevels'
import { recordKeys } from 'helpers/records'

export const queryMonthSchema = yup.object<{ month: MonthType }>().shape({
  month: yup.string().required().oneOf(recordKeys(dataParsing['months']))
})

export const queryTypeOrDistanceSchema = yup
  .object<{
    typeOrDistance: TypeOrDistanceType
  }>()
  .shape({
    typeOrDistance: yup
      .string()
      .required()
      .oneOf([...recordKeys(dataParsing['typeOrDistances']), 'run'] as const)
  })

export const queryLocationSchema = yup
  .object<{ location: LocationType }>()
  .shape({
    location: yup
      .string()
      .required()
      .oneOf<LocationType>([
        ...recordKeys(dataParsing['locations']['country']),
        ...recordKeys(dataParsing['locations']['countrySubdivisionLevel1']),
        ...recordKeys(dataParsing['locations']['countrySubdivisionLevel2'])
      ])
  })

export const capitalize = (s?: string) => s && s[0]?.toUpperCase() + s.slice(1)

export type MonthType = keyof (typeof dataParsing)['months']

type countrySubdivisionLevel1Type =
  keyof (typeof dataParsing)['locations']['countrySubdivisionLevel1']

type countrySubdivisionLevel2Type =
  keyof (typeof dataParsing)['locations']['countrySubdivisionLevel2']

type countryType = keyof (typeof dataParsing)['locations']['country']

export type LocationType =
  | countrySubdivisionLevel1Type
  | countrySubdivisionLevel2Type
  | countryType
export type TypeOrDistanceType =
  | keyof (typeof dataParsing)['typeOrDistances']
  | 'run'

export const getParsedData = ({
  location,
  month,
  typeOrDistance
}: {
  location?: LocationType
  month?: MonthType
  typeOrDistance?: TypeOrDistanceType
}) => {
  const { locations, typeOrDistances, months } = dataParsing
  const { countrySubdivisionLevel1, countrySubdivisionLevel2, country } =
    locations
  const parsedMonth = month ? months[month] : null
  const parsedTypeOrDistance =
    typeOrDistance && typeOrDistance !== 'run'
      ? typeOrDistances[typeOrDistance]
      : null

  const typeOrDistanceLabel = parsedTypeOrDistance?.label
  const typeOrDistanceAlgoliaTranslation = parsedTypeOrDistance?.label
  const typeOrDistanceKey = parsedTypeOrDistance?.key ?? ''

  const motherElement =
    Object.values(raceCategoriesLevel1).find(({ level2Categories }) =>
      level2Categories.includes(typeOrDistanceKey as never)
    ) ?? null

  const distances = isLevel1Category(typeOrDistanceKey)
    ? raceCategoriesLevel1[typeOrDistanceKey]?.level2Categories
    : ([typeOrDistanceKey] as const)

  const childElements: typeof distances | null =
    !motherElement && distances && distances.length > 0 && !!distances[0]
      ? distances
      : null

  const countryLocation =
    location && country.hasOwnProperty(location)
      ? country[location as countryType]
      : null

  if (!!location && !!countryLocation) {
    return {
      ...(parsedTypeOrDistance && {
        parsedTypeOrDistance: typeOrDistanceLabel
      }),
      parsedLocation: countryLocation,
      ...(parsedMonth !== null && { parsedMonth }),
      ...(distances && { distances }),
      parsedMother: undefined,
      parsedChild: undefined,
      typeOrDistanceKey: undefined,
      typeOrDistanceAlgoliaTranslation: undefined
    }
  }

  const countrySubdivisionLevel1Location =
    location && countrySubdivisionLevel1.hasOwnProperty(location)
      ? countrySubdivisionLevel1[location as countrySubdivisionLevel1Type]
      : null

  const countrySubdivisionLevel2Location =
    location && countrySubdivisionLevel2.hasOwnProperty(location)
      ? countrySubdivisionLevel2[location as countrySubdivisionLevel2Type]
      : null
  const isLocationSubLevel1 = !!countrySubdivisionLevel1Location
  const isLocationSubLevel2 = !!countrySubdivisionLevel2Location
  const isValidLocation = isLocationSubLevel1 || isLocationSubLevel2
  const parsedLocation = !isValidLocation
    ? null
    : isLocationSubLevel1
    ? countrySubdivisionLevel1Location
    : countrySubdivisionLevel2Location

  return {
    ...(parsedTypeOrDistance
      ? { parsedTypeOrDistance: typeOrDistanceLabel }
      : {}),
    ...(typeOrDistanceAlgoliaTranslation
      ? {
          typeOrDistanceAlgoliaTranslation: typeOrDistanceAlgoliaTranslation
        }
      : {}),
    ...(parsedLocation ? { parsedLocation } : {}),
    ...(parsedMonth !== null ? { parsedMonth } : {}),
    ...(typeOrDistanceKey ? { typeOrDistanceKey } : {}),
    ...(motherElement
      ? {
          parsedMother: motherElement
        }
      : {}),
    ...(childElements
      ? {
          parsedChild: childElements
        }
      : {}),
    ...(distances ? { distances } : {})
  }
}

export const getAllRegions = () =>
  recordKeys(dataParsing.locations.countrySubdivisionLevel1).map(
    (region) => dataParsing.locations.countrySubdivisionLevel1[region]
  )

export const getBaseUrl = (locale: string | undefined) => {
  return locale === 'fr' || !locale ? '' : `/${locale}`
}
